import { METRIC_TYPES } from "helpers/metrics/constants"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import { createExposureDrilldownGatherers } from "gatherers/contributors/drilldown-exposure"
import { createRiskDrilldownGatherers } from "gatherers/contributors/drilldown-risk"
import { createFactorSensitivityDrilldownGatherers } from "gatherers/contributors/drilldown-factor-sensitivity"
import { createBetaDrilldownGatherers } from "gatherers/contributors/drilldown-beta"
import { createPerformanceDrilldownGatherers } from "gatherers/contributors/drilldown-performance"
import { createContentSetDrilldownGatherers } from "gatherers/contributors/drilldown-content-set"
import { resolveDrilldownInstruments } from "connected-components/dashboard/drilldown/drilldown-instrument-series"
import type { DrilldownFrozenSnapshot, DrilldownSeed } from "connected-components/dashboard/drilldown/types"

export const NAMED_VIEW_LABELS: Record<string, string> = {
  performance: "Performance",
  risk: "Risk",
  exposure: "Exposure",
  beta: "Beta",
  "factor-sensitivity": "Predicted Factor Sensitivity",
  predictedFactorSensitivity: "Predicted Factor Sensitivity",
  historicalFactorSensitivity: "Historical Factor Sensitivity",
}

// The per-instrument option bag is assembled dynamically and forwarded to the view-specific
// gatherer factories, which each accept their own loosely-typed option shape. Typing it precisely
// would mean unifying all factory signatures — out of scope here; the public entry point below is
// what's strongly typed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GathererOptions = any

function createGatherersForInstrument(view: string, options: GathererOptions) {
  // risk & performance show factor exposure alongside their own metrics, but only when drilling
  // into a specific factor (not aggregate/total views).
  const withFactorExposure = (gatherers: unknown[]) =>
    options.selectedFactor
      ? [...createExposureDrilldownGatherers({ ...options, selectedMetricDisplay: null }), ...gatherers]
      : gatherers

  switch (view) {
    case "exposure":
      return createExposureDrilldownGatherers(options)
    case "risk":
      return withFactorExposure(createRiskDrilldownGatherers(options))
    case "performance":
      return withFactorExposure(createPerformanceDrilldownGatherers(options))
    case "beta":
      return createBetaDrilldownGatherers(options)
    case "factor-sensitivity":
    case "predictedFactorSensitivity":
      return createFactorSensitivityDrilldownGatherers({ ...options, factorSensitivityType: FACTOR_SENSITIVITY_TYPES.PREDICTED })
    case "historicalFactorSensitivity":
      return createFactorSensitivityDrilldownGatherers({ ...options, factorSensitivityType: FACTOR_SENSITIVITY_TYPES.HISTORICAL })
    default:
      // Content-set view — the view ID is the content-set ID.
      return createContentSetDrilldownGatherers({ ...options, contentSetId: view })
  }
}

export interface CreateDrilldownGatherersOptions {
  refreshKey?: number
  groupBy?: unknown
  classifications?: unknown[]
  isRfrAttributionEnabled?: boolean
  betaFields?: unknown
  contentSetMetricDisplay?: string | null
  currentIntradayBatchId?: string | null
  isIntradayView?: boolean
}

/**
 * Dispatches to the appropriate view-specific gatherer factory for each
 * instrument present in the frozen snapshot (original + optional comparison).
 */
export function createDrilldownGatherers(
  view: string,
  frozenSnapshot: DrilldownFrozenSnapshot,
  seed: DrilldownSeed,
  options: CreateDrilldownGatherersOptions = {},
) {
  const {
    refreshKey = 0,
    groupBy,
    classifications,
    isRfrAttributionEnabled = false,
    betaFields,
    contentSetMetricDisplay = null,
    currentIntradayBatchId = null,
    isIntradayView = false,
  } = options
  const { modelId, base, startDate } = frozenSnapshot
  const { selectedFactor, selectedCategory, selectedMetricDisplay } = seed
  const isContentSetView = !NAMED_VIEW_LABELS[view]

  const sharedOptions = {
    modelId,
    base,
    groupByOptions: groupBy ?? undefined,
    notHeldRollup: true,
    startDate,
    selectedFactor,
    selectedCategory,
    classifications,
    isRfrAttributionEnabled,
    // Performance/content-set need a scalar metricDisplay or the contributor tree stores objects
    // and every attribution cell renders blank. Performance matches the source's % vs $ (default %).
    ...(view === "performance" && { selectedMetricDisplay: selectedMetricDisplay ?? METRIC_TYPES.CUMULATIVE_PERCENT }),
    ...(isContentSetView && contentSetMetricDisplay != null && { selectedMetricDisplay: contentSetMetricDisplay }),
    // Beta interpolates betaFields into its GraphQL selection; without it the query errors out.
    ...(view === "beta" && { betaFields }),
    ...(refreshKey > 0 && { _refreshKey: refreshKey }),
  }

  // One gatherer set per resolved series — each gathered as-of its own date and under its own
  // suffix so the columns don't collide.
  const { series } = resolveDrilldownInstruments(frozenSnapshot)

  return series.flatMap((instrumentSeries) => {
    const isOriginalSeries = instrumentSeries.instrumentType === "original"

    // Intraday routing applies to the ORIGINAL series only; the rebalanced/comparison second series
    // stays EOD on purpose — the frozen snapshot doesn't carry the end-vs-start same-day rules that
    // gate second-instrument intraday, and combined intraday modes are out of scope here. For the
    // original series: every view's gatherer accepts a nullable batchId (intradaySimulationBatch for
    // most views, intradaySimulation for performance), so a live/unpinned session (batchId: null)
    // routes intraday same as a pinned one. A pinned performance batch drops to the standard EOD
    // query, which op-hops compounds the committed batch into so benchmark base and not-held
    // contributors line up — that fallback lives inside performance's own gatherer, not here.
    const intradayOptions = isOriginalSeries && isIntradayView
      ? {
        shouldUseIntradayQuery: true,
        on: instrumentSeries.asOf,
        batchId: currentIntradayBatchId,
      }
      : {}

    return createGatherersForInstrument(view, {
      ...sharedOptions,
      ...intradayOptions,
      // composition/holdings/exposure query `to: endDate`; using this series' as-of date is how the
      // end-vs-start start column gets distinct start-date data.
      endDate: instrumentSeries.asOf,
      positionSet: instrumentSeries.positionSet,
      instrumentSuffix: instrumentSeries.suffix,
    })
  })
}
